/**
 * Phase 10 — Admin Enterprise Inquiries page.
 * Uses:
 *   GET   /api/admin/enterprise/inquiries?status=&business_type=&search=
 *   GET   /api/admin/enterprise/inquiries/{id}
 *   PATCH /api/admin/enterprise/inquiries/{id}/review
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Building2, Search, User, Archive, CheckCircle2, XCircle, MailCheck, FileSignature, UserCheck, Download, Handshake, PlayCircle, Flag } from 'lucide-react';
import theme from '../theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const cardStyle = {
  background: theme.colors.bg.card,
  border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.card, padding: 20,
};
const inputStyle = {
  padding: '8px 10px', border: `1px solid ${theme.colors.borders}`,
  borderRadius: theme.radius.sm, fontSize: 13, outline: 'none',
  background: theme.colors.bg.card, color: theme.colors.text.primary,
};

const STATUS_COLOR = {
  submitted: '#6B7280', under_review: '#3B82F6', contacted: '#8B5CF6',
  qualified: '#059669', proposal_sent: '#0EA5E9',
  approved: theme.colors.status.success, rejected: theme.colors.status.error,
  archived: theme.colors.text.muted,
};

const ACTIONS = [
  { key: 'mark_under_review',  label: 'Under Review',  icon: Search,        color: '#3B82F6' },
  { key: 'mark_contacted',     label: 'Contacted',     icon: MailCheck,     color: '#8B5CF6' },
  { key: 'mark_qualified',     label: 'Qualified',     icon: UserCheck,     color: '#059669' },
  { key: 'mark_proposal_sent', label: 'Proposal Sent', icon: FileSignature, color: '#0EA5E9' },
  { key: 'approve',            label: 'Approve',       icon: CheckCircle2,  color: theme.colors.status.success },
  { key: 'reject',             label: 'Reject',        icon: XCircle,       color: theme.colors.status.error },
  { key: 'archive',            label: 'Archive',       icon: Archive,       color: theme.colors.text.muted },
  { key: 'assign_to_me',       label: 'Assign to Me',  icon: User,          color: theme.colors.primary },
];

// Phase 10B — separate handoff actions (never trigger billing/PMPro).
const HANDOFF_ACTIONS = [
  { key: 'mark_ready_for_manual_onboarding', label: 'Ready for Manual Onboarding', icon: Handshake,   color: '#7C3AED' },
  { key: 'mark_manual_onboarding_started',   label: 'Manual Onboarding Started',   icon: PlayCircle,  color: '#0891B2' },
  { key: 'mark_handoff_completed',           label: 'Handoff Completed',           icon: Flag,        color: '#059669' },
];

const HANDOFF_STATUS_COLOR = {
  not_started: '#6B7280',
  ready_for_manual_onboarding: '#7C3AED',
  manual_onboarding_started: '#0891B2',
  completed: '#059669',
};

export default function AdminEnterpriseInquiriesPage() {
  const [rows, setRows] = useState([]);
  const [statusOpts, setStatusOpts] = useState([]);
  const [filters, setFilters] = useState({ status: '', business_type: '', search: '' });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [notesDraft, setNotesDraft] = useState('');
  const [handoffDraft, setHandoffDraft] = useState('');
  const auth = useMemo(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  }), []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.business_type) params.business_type = filters.business_type;
      if (filters.search) params.search = filters.search;
      const res = await axios.get(`${API}/admin/enterprise/inquiries`, { ...auth, params });
      setRows(res.data?.inquiries || []);
      setStatusOpts(res.data?.status_options || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load inquiries');
    } finally { setLoading(false); }
  }, [auth, filters]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    try {
      const res = await axios.get(`${API}/admin/enterprise/inquiries/${id}`, auth);
      setSelected(res.data?.inquiry);
      setNotesDraft('');
      setHandoffDraft('');
    } catch (e) {
      toast.error('Failed to load inquiry');
    }
  };

  const runAction = async (action) => {
    if (!selected) return;
    try {
      const body = { action };
      if (notesDraft.trim()) body.admin_notes = notesDraft.trim();
      if (handoffDraft.trim()) body.handoff_notes = handoffDraft.trim();
      const res = await axios.patch(
        `${API}/admin/enterprise/inquiries/${selected.id}/review`, body, auth,
      );
      setSelected(res.data?.inquiry);
      setNotesDraft('');
      setHandoffDraft('');
      toast.success(`Action applied: ${action}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    }
  };

  const exportCsv = async () => {
    try {
      const params = { csv: 1 };
      if (filters.status) params.status = filters.status;
      if (filters.business_type) params.business_type = filters.business_type;
      if (filters.search) params.search = filters.search;
      const res = await axios.get(`${API}/admin/enterprise/inquiries`, {
        ...auth, params, responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `enterprise_inquiries_${Date.now()}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (e) {
      toast.error('CSV export failed');
    }
  };

  return (
    <div data-testid="admin-enterprise-inquiries-page" style={{ padding: 40 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 6 }}>
        <Building2 size={24} color={theme.colors.primary} />
        <h1 style={{ fontSize: 26, fontWeight: 800, margin: 0,
          fontFamily: theme.fonts.heading, color: theme.colors.text.primary }}>
          Enterprise Inquiries
        </h1>
      </div>
      <p style={{ fontSize: 13, color: theme.colors.text.secondary, marginBottom: 18 }}>
        Review Enterprise requests, track follow-up status, and manage manual
        approval notes.
      </p>

      <div style={{ ...cardStyle, marginBottom: 16, display: 'flex',
        gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <select data-testid="admin-ent-filter-status" style={{ ...inputStyle, width: 200 }}
          value={filters.status}
          onChange={e => setFilters({ ...filters, status: e.target.value })}>
          <option value="">All statuses</option>
          {statusOpts.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <input data-testid="admin-ent-filter-business-type" style={{ ...inputStyle, width: 200 }}
          placeholder="Business type filter"
          value={filters.business_type}
          onChange={e => setFilters({ ...filters, business_type: e.target.value })} />
        <input data-testid="admin-ent-filter-search" style={{ ...inputStyle, flex: 1, minWidth: 240 }}
          placeholder="Search company / email / name"
          value={filters.search}
          onChange={e => setFilters({ ...filters, search: e.target.value })} />
        <button data-testid="admin-ent-export-csv" onClick={exportCsv}
          style={{ padding: '8px 14px', borderRadius: theme.radius.sm, border: 'none',
            background: theme.colors.primary, color: '#fff', cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 700,
            fontSize: 13 }}>
          <Download size={14} /> Export CSV
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 16 }}>
        {/* List */}
        <div data-testid="admin-ent-list" style={cardStyle}>
          {loading ? (
            <div style={{ color: theme.colors.text.muted, padding: 12 }}>Loading…</div>
          ) : rows.length === 0 ? (
            <div data-testid="admin-ent-empty" style={{ color: theme.colors.text.muted, padding: 12 }}>
              No inquiries match your filters.
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 8 }}>
              {rows.map(r => {
                const color = STATUS_COLOR[r.status] || '#6B7280';
                const isSelected = selected?.id === r.id;
                return (
                  <button key={r.id}
                    data-testid={`admin-ent-row-${r.id}`}
                    onClick={() => openDetail(r.id)}
                    style={{ textAlign: 'left', padding: 12,
                      borderRadius: theme.radius.sm,
                      border: `1px solid ${isSelected ? theme.colors.primary : theme.colors.borders}`,
                      background: theme.colors.bg.secondary, cursor: 'pointer' }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <div style={{ fontWeight: 700, color: theme.colors.text.primary }}>
                        {r.company_name}
                      </div>
                      <span style={{ padding: '2px 8px', borderRadius: 999,
                        background: `${color}1A`, color, fontSize: 10,
                        fontWeight: 800, textTransform: 'uppercase' }}>
                        {r.status}
                      </span>
                    </div>
                    <div style={{ fontSize: 11, color: theme.colors.text.muted, marginTop: 4 }}>
                      {r.requester_name} · {r.requester_email}
                      {r.business_type ? ` · ${r.business_type}` : ''}
                    </div>
                    <div style={{ fontSize: 10, color: theme.colors.text.muted, marginTop: 4 }}>
                      {r.created_at ? new Date(r.created_at).toLocaleString() : '—'}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Detail */}
        <div data-testid="admin-ent-detail" style={cardStyle}>
          {!selected ? (
            <div style={{ color: theme.colors.text.muted, padding: 12 }}>
              Select an inquiry to view details.
            </div>
          ) : (
            <div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: theme.colors.text.primary }}>
                  {selected.company_name}
                </div>
                <span style={{ padding: '2px 8px', borderRadius: 999,
                  background: `${STATUS_COLOR[selected.status] || '#6B7280'}1A`,
                  color: STATUS_COLOR[selected.status] || '#6B7280', fontSize: 10,
                  fontWeight: 800, textTransform: 'uppercase' }}>
                  {selected.status}
                </span>
              </div>
              <div style={{ fontSize: 12, color: theme.colors.text.muted, marginTop: 4 }}>
                {selected.requester_name} · {selected.requester_email}
                {selected.phone ? ` · ${selected.phone}` : ''}
              </div>
              {selected.website_or_social && (
                <div style={{ fontSize: 12, marginTop: 6 }}>
                  <b>Link:</b> {selected.website_or_social}
                </div>
              )}
              {selected.business_type && (
                <div style={{ fontSize: 12, marginTop: 4 }}>
                  <b>Business type:</b> {selected.business_type}
                </div>
              )}
              {selected.expected_volume && (
                <div style={{ fontSize: 12, marginTop: 4 }}>
                  <b>Expected volume:</b> {selected.expected_volume}
                </div>
              )}
              {selected.budget_range && (
                <div style={{ fontSize: 12, marginTop: 4 }}>
                  <b>Budget:</b> {selected.budget_range}
                </div>
              )}
              {selected.timeline && (
                <div style={{ fontSize: 12, marginTop: 4 }}>
                  <b>Timeline:</b> {selected.timeline}
                </div>
              )}
              {selected.use_case && (
                <div style={{ fontSize: 12, marginTop: 8 }}>
                  <b>Use case:</b> <span style={{ whiteSpace: 'pre-wrap' }}>{selected.use_case}</span>
                </div>
              )}
              {selected.requested_features && (
                <div style={{ fontSize: 12, marginTop: 8 }}>
                  <b>Requested features:</b> <span style={{ whiteSpace: 'pre-wrap' }}>{selected.requested_features}</span>
                </div>
              )}
              {selected.notes && (
                <div style={{ fontSize: 12, marginTop: 8 }}>
                  <b>Submitter notes:</b> <span style={{ whiteSpace: 'pre-wrap' }}>{selected.notes}</span>
                </div>
              )}

              <div style={{ marginTop: 14, borderTop: `1px dashed ${theme.colors.borders}`, paddingTop: 10 }}>
                <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 6 }}>Admin notes (append)</div>
                <textarea data-testid="admin-ent-notes-input" style={{ ...inputStyle, width: '100%', minHeight: 60, fontFamily: 'inherit' }}
                  placeholder="Add a note (optional) — will be stamped with your email + timestamp"
                  value={notesDraft} onChange={e => setNotesDraft(e.target.value)} />
                {selected.admin_notes && (
                  <div style={{ fontSize: 11, color: theme.colors.text.muted,
                    background: theme.colors.bg.secondary, padding: 8,
                    borderRadius: 4, marginTop: 6, whiteSpace: 'pre-wrap', maxHeight: 180, overflow: 'auto' }}>
                    <b>History:</b>{'\n'}{selected.admin_notes}
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 12 }}>
                {ACTIONS.map(a => (
                  <button key={a.key} data-testid={`admin-ent-action-${a.key}`}
                    onClick={() => runAction(a.key)}
                    style={{ padding: '6px 10px', border: `1px solid ${a.color}`,
                      background: `${a.color}12`, color: a.color, cursor: 'pointer',
                      borderRadius: theme.radius.sm, fontSize: 12, fontWeight: 700,
                      display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    <a.icon size={12} /> {a.label}
                  </button>
                ))}
              </div>

              {/* Phase 10B — Manual Handoff */}
              <div data-testid="admin-ent-handoff-section"
                style={{ marginTop: 16, borderTop: `1px solid ${theme.colors.borders}`,
                  paddingTop: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8,
                  marginBottom: 6, flexWrap: 'wrap' }}>
                  <Handshake size={14} color={theme.colors.primary} />
                  <div style={{ fontWeight: 800, fontSize: 13, color: theme.colors.text.primary }}>
                    Manual Handoff
                  </div>
                  <span data-testid="admin-ent-handoff-status"
                    style={{ padding: '2px 8px', borderRadius: 999,
                      background: `${HANDOFF_STATUS_COLOR[selected.handoff_status] || '#6B7280'}1A`,
                      color: HANDOFF_STATUS_COLOR[selected.handoff_status] || '#6B7280',
                      fontSize: 10, fontWeight: 800, textTransform: 'uppercase' }}>
                    {selected.handoff_status || 'not_started'}
                  </span>
                  {selected.handoff_at && (
                    <span style={{ fontSize: 11, color: theme.colors.text.muted }}>
                      · {new Date(selected.handoff_at).toLocaleString()}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: theme.colors.text.muted, marginBottom: 8 }}>
                  Manual handoff never creates an Enterprise membership. Use these
                  actions to signal onboarding progress; then complete the customer
                  setup in the admin dashboard.
                </div>
                {selected.handoff_url && (
                  <a data-testid="admin-ent-handoff-url" href={selected.handoff_url}
                    style={{ display: 'inline-block', marginBottom: 8, fontSize: 12,
                      color: theme.colors.primary, textDecoration: 'underline' }}>
                    Open manual onboarding surface
                  </a>
                )}
                {selected.handoff_notes && (
                  <div style={{ fontSize: 11, color: theme.colors.text.muted,
                    background: theme.colors.bg.secondary, padding: 8,
                    borderRadius: 4, marginBottom: 8, whiteSpace: 'pre-wrap',
                    maxHeight: 160, overflow: 'auto' }}>
                    <b>Handoff history:</b>{'\n'}{selected.handoff_notes}
                  </div>
                )}
                <textarea data-testid="admin-ent-handoff-notes-input"
                  style={{ ...inputStyle, width: '100%', minHeight: 50, fontFamily: 'inherit' }}
                  placeholder="Handoff note (optional) — appended, stamped with your email"
                  value={handoffDraft} onChange={e => setHandoffDraft(e.target.value)} />
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                  {HANDOFF_ACTIONS.map(a => (
                    <button key={a.key} data-testid={`admin-ent-action-${a.key}`}
                      onClick={() => runAction(a.key)}
                      style={{ padding: '6px 10px', border: `1px solid ${a.color}`,
                        background: `${a.color}12`, color: a.color, cursor: 'pointer',
                        borderRadius: theme.radius.sm, fontSize: 12, fontWeight: 700,
                        display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                      <a.icon size={12} /> {a.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
